import { Box, File, FileText, type LucideIcon } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Icon } from '../../components/ui/Icon';
import { Text, typography } from '../../components/ui/Text';
import { cx } from '../../components/ui/cx';
import { useT } from '../i18n';
import { ownerLabel, resultTypeKey, type SearchHit } from './searchModel';

export function iconForType(type: string): LucideIcon {
  if (type === 'software-catalog') return Box;
  if (type === 'techdocs') return FileText;
  return File;
}

export interface SearchResultItemProps {
  hit: SearchHit;
  /** Keyboard-highlighted row (command-palette navigation). */
  highlighted?: boolean;
  onSelect: (hit: SearchHit) => void;
}

/**
 * One search result row on the design system: leading type icon, title, a
 * result-type badge, a truncated snippet and owner/kind metadata. Pure props —
 * the container decides what `onSelect` navigates to.
 */
export const SearchResultItem = ({
  hit,
  highlighted = false,
  onSelect,
}: SearchResultItemProps) => {
  const t = useT();
  const typeKey = resultTypeKey(hit.type);
  const typeLabel = typeKey ? t(typeKey) : hit.type;
  return (
    <button
      type="button"
      aria-label={hit.title}
      data-highlighted={highlighted || undefined}
      onClick={() => onSelect(hit)}
      className={cx(
        'flex w-full appearance-none items-start gap-3 rounded-lg px-3 py-2.5 text-left outline-none transition',
        'hover:bg-muted focus-visible:bg-muted',
        highlighted ? 'bg-muted' : 'bg-transparent',
      )}
    >
      <span className="mt-0.5 inline-flex text-fg-muted">
        <Icon as={iconForType(hit.type)} size="md" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className={cx(typography.label, 'truncate text-fg')}>
            {hit.title}
          </span>
          <Badge color="gray" size="sm">
            {typeLabel}
          </Badge>
        </span>
        {hit.text && (
          <Text variant="caption" tone="tertiary" className="line-clamp-1">
            {hit.text}
          </Text>
        )}
        {(hit.kind || hit.owners?.length) && (
          <span className="flex items-center gap-2">
            {hit.kind && (
              <Text variant="caption" tone="muted">
                {hit.kind}
              </Text>
            )}
            {hit.owners?.length ? (
              <Text variant="caption" tone="muted">
                · {hit.owners.map(ownerLabel).join(', ')}
              </Text>
            ) : null}
          </span>
        )}
      </span>
    </button>
  );
};
